/**
 * CreditCounter Component
 *
 * Displays the current credit count as a number glyph centered
 * on a colored circle. Circle color reflects urgency:
 *   10-5: green (#387c47)
 *   4-3:  gold (#d1930b)
 *   2-1:  red-orange (#b84927)
 *   0:    medium-brown (#514b44)
 *
 * Animates credit decreases: current counter shrinks, new counter
 * drops in from above. Multi-credit changes play sequentially.
 */

import React from 'react';
import NumberGlyphs from '../svg/numbers';
import {
  useCreditAnimation,
  CREDIT_SHRINK_SCALE,
  CREDIT_SHRINK_DURATION,
  CREDIT_DROP_DURATION,
} from '../hooks/useCreditAnimation';

interface CreditCounterProps {
  credits: number;
  /** Called when a credit decrement animation starts */
  onAnimStart?: () => void;
  /** Called when a credit decrement animation ends */
  onAnimEnd?: () => void;
}

const CIRCLE_SIZE = 116;
// Uniform scale applied to all number glyphs so individual digits
// are the same size in single-digit and double-digit numbers.
const NUMBER_SCALE = 0.75;

function getCircleColor(credits: number): string {
  if (credits >= 5) return '#387c47';
  if (credits >= 3) return '#d1930b';
  if (credits >= 1) return '#b84927';
  return '#514b44';
}

function renderCounter(credit: number) {
  const clamped = Math.max(0, Math.min(20, credit));
  const Glyph = NumberGlyphs[clamped];
  const color = getCircleColor(clamped);

  return (
    <>
      <div style={{
        position: 'absolute',
        top: 0, left: 0,
        width: CIRCLE_SIZE, height: CIRCLE_SIZE,
        borderRadius: '50%',
        backgroundColor: color,
      }} />
      <div style={{
        position: 'absolute',
        top: 0, left: 0,
        width: CIRCLE_SIZE, height: CIRCLE_SIZE,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Glyph style={{ display: 'block', transform: `scale(${NUMBER_SCALE})` }} />
      </div>
    </>
  );
}

const CreditCounter: React.FC<CreditCounterProps> = ({ credits, onAnimStart, onAnimEnd }) => {
  const { phase, displayedCredit, incomingCredit, onShrinkEnd, onDropEnd } = useCreditAnimation(credits);

  // Fire callbacks when animation starts/ends
  const wasAnimating = React.useRef(false);
  React.useEffect(() => {
    const animating = phase !== 'idle';
    if (animating && !wasAnimating.current) {
      wasAnimating.current = true;
      onAnimStart?.();
    } else if (!animating && wasAnimating.current) {
      wasAnimating.current = false;
      onAnimEnd?.();
    }
  }, [phase, onAnimStart, onAnimEnd]);

  const isShrunk = phase === 'shrinking' || phase === 'pre-drop' || phase === 'dropping';
  const showIncoming = phase === 'pre-drop' || phase === 'dropping';

  return (
    <div style={{
      position: 'relative',
      width: CIRCLE_SIZE,
      height: CIRCLE_SIZE,
      overflow: 'hidden',
    }}>
      {/* Displayed counter — shrinks during animation */}
      <div
        style={{
          position: 'absolute',
          top: 0, left: 0,
          width: CIRCLE_SIZE,
          height: CIRCLE_SIZE,
          transform: `scale(${isShrunk ? CREDIT_SHRINK_SCALE : 1})`,
          transition: phase === 'shrinking'
            ? `transform ${CREDIT_SHRINK_DURATION}ms ease` : 'none',
          transformOrigin: 'center center',
        }}
        onTransitionEnd={(e) => {
          if (e.propertyName === 'transform' && e.target === e.currentTarget) {
            onShrinkEnd();
          }
        }}
      >
        {renderCounter(displayedCredit)}
      </div>

      {/* Incoming counter — drops from above */}
      {showIncoming && (
        <div
          style={{
            position: 'absolute',
            top: 0, left: 0,
            width: CIRCLE_SIZE,
            height: CIRCLE_SIZE,
            transform: `translateY(${phase === 'dropping' ? 0 : -CIRCLE_SIZE}px)`,
            transition: phase === 'dropping'
              ? `transform ${CREDIT_DROP_DURATION}ms ease` : 'none',
            zIndex: 1,
          }}
          onTransitionEnd={(e) => {
            if (e.propertyName === 'transform' && e.target === e.currentTarget) {
              onDropEnd();
            }
          }}
        >
          {renderCounter(incomingCredit)}
        </div>
      )}
    </div>
  );
};

export default CreditCounter;
